import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, Loader2, ArrowUpDown, ArrowUp, ArrowDown, UserMinus, UserCheck } from "lucide-react";
import { api } from "@/lib/api";
import { trunc, cnpjFmt, btn, Tip, Toolbar, Paginacao, useBusca, Cabecalho } from "@/pages/comprovantes/ui";
 
const POR_PAGINA = 50;
const ROTA_BASE = "/app/dashboard-federal";
const fmtData = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
 
const BADGE = {
  excluida: "border-destructive/20 bg-destructive/10 text-destructive",
  optante: "border-success/20 bg-success/10 text-success",
};
 
export default function ExclusaoSimplesPage() {
  const navigate = useNavigate();
  const qs = new URLSearchParams(useLocation().search);
  const [regime, setRegime] = useState(qs.get("regime") === "mei" ? "mei" : "simples");
  const [status, setStatus] = useState(qs.get("status") || "todos");
  const [dados, setDados] = useState({ registros: [], total_empresas: 0, consultadas: 0, atualizacao: {} });
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [ordem, setOrdem] = useState({ key: "razao_social", asc: true });
  const [visiveis, setVisiveis] = useState({});
  const poll = useRef(null);
 
  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    try {
      const { data } = await api.get("/simples-nacional/exclusao", { params: { regime } });
      setDados(data);
    } catch (_) { /* mantém dados anteriores */ }
    finally { setCarregando(false); }
  }, [regime]);
 
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => setPagina(1), [busca, regime, status]);
 
  const atualizando = dados.atualizacao?.atualizando || (dados.total_empresas > 0 && dados.consultadas < dados.total_empresas);
  useEffect(() => {
    clearInterval(poll.current);
    if (atualizando) poll.current = setInterval(() => carregar(true), 4000);
    return () => clearInterval(poll.current);
  }, [atualizando, carregar]);
 
  const forcar = async () => {
    try { await api.post("/simples-nacional/exclusao/atualizar"); } catch (_) { /* noop */ }
    setTimeout(() => carregar(true), 1500);
  };
 
  const todos = dados.registros || [];
  const nExcl = todos.filter((r) => r.status === "excluida").length;
  const nOpt = todos.length - nExcl;
 
  const colunas = [
    { key: "razao_social", label: "Razão Social", fixa: true },
    { key: "cnpj", label: "CNPJ", exportar: (l) => cnpjFmt(l.cnpj) },
    { key: "data_inicial", label: "Data Inicial", exportar: (l) => fmtData(l.data_inicial) },
    { key: "data_final", label: "Data Final", exportar: (l) => fmtData(l.data_final) },
    { key: "status", label: "Status", exportar: (l) => (l.status === "excluida" ? "Excluída" : "Optante") },
  ];
  const vis = (k) => visiveis[k] !== false;
  const nCols = colunas.filter((c) => vis(c.key)).length;
 
  const base = status === "todos" ? todos : todos.filter((r) => r.status === status);
  const filtradas = useBusca(base, ["razao_social", "cnpj"], busca);
  const ordenadas = [...filtradas].sort((a, b) => {
    const r = String(a[ordem.key] || "").localeCompare(String(b[ordem.key] || ""), "pt-BR");
    return ordem.asc ? r : -r;
  });
  const paginaLinhas = ordenadas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
 
  const Ord = ({ k, label, align = "left" }) => {
    const ativo = ordem.key === k;
    const Icone = !ativo ? ArrowUpDown : ordem.asc ? ArrowUp : ArrowDown;
    return (
      <button onClick={() => setOrdem((o) => ({ key: k, asc: o.key === k ? !o.asc : true }))}
        className={`inline-flex items-center gap-1 uppercase hover:text-foreground ${ativo ? "text-foreground" : ""} ${align === "center" ? "justify-center" : ""}`}>
        {label} <Icone className={`h-3 w-3 ${ativo ? "text-primary" : "opacity-60"}`} />
      </button>
    );
  };
 
  const titulo = regime === "mei" ? "Exclusão do MEI" : "Exclusão do Simples Nacional";
  const Resumo = ({ valor, label, desc, Icone, cor, alvo }) => {
    const ativo = status === alvo;
    return (
      <button onClick={() => setStatus(ativo ? "todos" : alvo)}
        className={`rounded-2xl border bg-card p-4 text-left transition ${ativo ? "border-primary/50 ring-1 ring-primary/20" : "border-border hover:border-primary/30"}`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icone className={`h-4 w-4 ${cor}`} strokeWidth={1.8} />
        </div>
        <p className="mt-2 font-display text-2xl tabular-nums text-foreground">{carregando ? "—" : valor}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">{desc}</p>
      </button>
    );
  };
 
  return (
    <div className="space-y-6">
      <Cabecalho titulo={titulo}
        trilha={[{ label: "Dashboard Federal", onClick: () => navigate(ROTA_BASE) }, { label: titulo }]}>
        <div className="inline-flex items-center rounded-xl border border-border bg-card p-1">
          {[["simples", "Simples Nacional"], ["mei", "MEI"]].map(([v, l]) => (
            <button key={v} onClick={() => setRegime(v)}
              className={`h-8 rounded-lg px-3 text-sm font-medium transition ${regime === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        <button onClick={forcar} disabled={dados.atualizacao?.atualizando} className={btn.primary}>
          {dados.atualizacao?.atualizando ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Atualizar
        </button>
      </Cabecalho>
 
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Resumo alvo="excluida" valor={nExcl} label="Excluídas" desc={`Empresas excluídas do ${regime === "mei" ? "MEI" : "Simples"}`} Icone={UserMinus} cor="text-destructive/70" />
        <Resumo alvo="optante" valor={nOpt} label="Optantes" desc="Empresas ativas no regime" Icone={UserCheck} cor="text-success/70" />
        <div className="col-span-2 rounded-2xl border border-border bg-card p-4 lg:col-span-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Empresas verificadas</span>
          <p className="mt-2 font-display text-2xl tabular-nums text-foreground">
            {carregando ? "—" : dados.consultadas}<span className="text-sm text-muted-foreground"> / {dados.total_empresas}</span>
          </p>
          <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
            {atualizando ? <><Loader2 className="h-3 w-3 animate-spin" /> Consultando a Receita Federal...</> : "Dados da Receita Federal"}
          </p>
        </div>
      </section>
 
      <section className="rounded-2xl border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <Toolbar colunas={colunas} visiveis={visiveis} setVisiveis={setVisiveis} linhas={ordenadas}
            nome={regime === "mei" ? "exclusao_mei" : "exclusao_simples"} busca={busca} setBusca={setBusca} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {vis("razao_social") && <th className="px-5 py-3 text-left"><Ord k="razao_social" label="Razão Social" /></th>}
                {vis("cnpj") && <th className="px-3 py-3 text-left"><Ord k="cnpj" label="CNPJ" /></th>}
                {vis("data_inicial") && <th className="px-3 py-3 text-left"><Ord k="data_inicial" label="Data Inicial" /></th>}
                {vis("data_final") && <th className="px-3 py-3 text-left"><Ord k="data_final" label="Data Final" /></th>}
                {vis("status") && <th className="w-32 px-5 py-3 text-center"><Ord k="status" label="Status" align="center" /></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {carregando && (
                <tr><td colSpan={nCols} className="py-14 text-center text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando empresas...</span>
                </td></tr>
              )}
              {!carregando && paginaLinhas.length === 0 && (
                <tr><td colSpan={nCols} className="py-14 text-center text-sm text-muted-foreground">
                  {atualizando ? "Consultando a Receita Federal..." : "Nenhuma empresa encontrada."}
                </td></tr>
              )}
              {!carregando && paginaLinhas.map((l) => (
                <tr key={l.cnpj} className="transition-colors hover:bg-muted/30">
                  {vis("razao_social") && <td className="px-5 py-3"><p className="font-medium text-foreground" title={l.razao_social}>{trunc(l.razao_social)}</p></td>}
                  {vis("cnpj") && <td className="px-3 py-3 font-mono text-xs text-muted-foreground">{cnpjFmt(l.cnpj)}</td>}
                  {vis("data_inicial") && <td className="px-3 py-3 tabular-nums text-muted-foreground">{fmtData(l.data_inicial) || "—"}</td>}
                  {vis("data_final") && <td className="px-3 py-3 tabular-nums text-muted-foreground">{fmtData(l.data_final) || "—"}</td>}
                  {vis("status") && (
                    <td className="px-5 py-3 text-center">
                      <Tip text={l.status === "excluida" ? `Empresa excluída desde ${fmtData(l.data_final)}` : `Empresa optante desde ${fmtData(l.data_inicial)}`}>
                        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${BADGE[l.status]}`}>
                          {l.status === "excluida" ? "Excluída" : "Optante"}
                        </span>
                      </Tip>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao pagina={pagina} setPagina={setPagina} total={ordenadas.length} porPagina={POR_PAGINA} />
      </section>
    </div>
  );
}
